/**
 * LawBee subscription plans: the single source of truth for what each plan costs and unlocks.
 * The API enforces it, the web app renders it, Stripe prices must match it (lookup keys) and it is
 * the LawBee slice of WRKRBZ's `_shared/catalog.json` (version 2026-10-15). Do not change prices
 * here without changing the catalog first (Hive owns it).
 *
 * Two ways a workspace gets a plan:
 *  - standalone: own sign-up with a 7-day trial and LawBee's own Stripe Checkout (like BuildBee);
 *  - Hive-managed: Hive provisions the workspace and pushes entitlements (plan, features, limits,
 *    status); LawBee enforces the last push locally and never opens Checkout for its own plan.
 */

export const CATALOG_VERSION = '2026-10-15';

export const PAID_PLANS = ['law_starter', 'law_growth'] as const;
export type PaidPlanId = (typeof PAID_PLANS)[number];
export type PlanId = 'trial' | PaidPlanId;

/** Every feature key in the catalog for LawBee. */
export const PLAN_FEATURES = [
  'intake_forms',
  'matters',
  'case_updates',
  'booking',
  'payments',
  'sms_updates',
  'deadline_checkins',
  'document_requests',
  'client_portal',
  'integrations',
] as const;
export type PlanFeature = (typeof PLAN_FEATURES)[number];

/** Features that only Growth includes (used for upgrade prompts). */
export const GROWTH_ONLY_FEATURES: readonly PlanFeature[] = ['sms_updates', 'deadline_checkins', 'document_requests', 'client_portal', 'integrations'];

export const PLAN_FEATURE_LABELS: Record<PlanFeature, string> = {
  intake_forms: 'Online intake forms',
  matters: 'Matters',
  case_updates: 'Case status updates',
  booking: 'Consultation booking',
  payments: 'Payments',
  sms_updates: 'SMS case updates',
  deadline_checkins: 'Deadline and inactivity check-ins',
  document_requests: 'Document requests',
  client_portal: 'Client portal',
  integrations: 'Practice-management integrations',
};

export type PlanLimits = {
  /** Team members (seats); null = unlimited. */
  users: number | null;
  /** Matters with status open at the same time; null = unlimited. */
  openMatters: number | null;
};

export type PlanDefinition = {
  id: PaidPlanId;
  name: string;
  tagline: string;
  /** Per firm per month, CAD cents. */
  priceCents: number;
  currency: 'cad';
  interval: 'month';
  lookupKey: string;
  limits: PlanLimits;
  features: readonly PlanFeature[];
  highlights: readonly string[];
  badge?: string;
};

export const PLANS: Record<PaidPlanId, PlanDefinition> = {
  law_starter: {
    id: 'law_starter',
    name: 'LawBee Starter',
    tagline: 'Intake, case updates, bookings and payments for a small practice.',
    priceCents: 34_900,
    currency: 'cad',
    interval: 'month',
    lookupKey: 'lawbee_starter_monthly',
    limits: { users: 3, openMatters: 100 },
    features: ['intake_forms', 'matters', 'case_updates', 'booking', 'payments'],
    highlights: [
      'Online intake forms per matter type',
      'Case status updates by email',
      'Consultation booking with reminders',
      'Retainers and fees via Stripe',
      'Up to 3 team members',
    ],
  },
  law_growth: {
    id: 'law_growth',
    name: 'LawBee Growth',
    tagline: 'Everything in Starter, plus check-ins, SMS, a client portal and integrations.',
    priceCents: 74_900,
    currency: 'cad',
    interval: 'month',
    lookupKey: 'lawbee_growth_monthly',
    limits: { users: null, openMatters: null },
    features: PLAN_FEATURES,
    highlights: [
      'Everything in Starter',
      'Unlimited team and matters',
      'SMS updates',
      'Deadline and inactivity check-ins',
      'Document requests and client portal',
      'Practice-management integrations',
    ],
    badge: 'Most complete',
  },
};

export const TRIAL_DAYS = 7;

export function isPaidPlan(plan: string | null | undefined): plan is PaidPlanId {
  return plan === 'law_starter' || plan === 'law_growth';
}

export function planForLookupKey(lookupKey: string | null | undefined): PaidPlanId | null {
  return PAID_PLANS.find((p) => PLANS[p].lookupKey === lookupKey) ?? null;
}

/**
 * Standalone (non-Hive) Stripe prices can live in the same Stripe account as Hive's, which owns
 * the catalog lookup keys, so standalone prices use `standalone_<catalog key>`.
 */
export const standaloneLookupKey = (catalogLookupKey: string) => `standalone_${catalogLookupKey}`;

export function planForStandaloneLookupKey(lookupKey: string | null | undefined): PaidPlanId | null {
  return PAID_PLANS.find((p) => standaloneLookupKey(PLANS[p].lookupKey) === lookupKey) ?? null;
}

const CATALOG_PLAN_CENTS = Object.fromEntries(PAID_PLANS.map((id) => [id, PLANS[id].priceCents])) as Record<PaidPlanId, number>;

/** The standalone Stripe lookup keys whose live amounts LawBee shows and charges. */
export function standaloneLookupKeys(): string[] {
  return PAID_PLANS.map((id) => standaloneLookupKey(PLANS[id].lookupKey));
}

/**
 * Prices are edited on Hive's Pricing page, which creates new standalone Stripe prices. The API
 * reads their amounts (by lookup key) and applies them here, so PLANS and every screen built on
 * them match what Checkout charges. A key missing from `amounts` uses the catalog price.
 */
export function applyStandalonePrices(amounts: Readonly<Record<string, number>>): void {
  for (const id of PAID_PLANS) (PLANS[id] as { priceCents: number }).priceCents = amounts[standaloneLookupKey(PLANS[id].lookupKey)] ?? CATALOG_PLAN_CENTS[id];
}

export type BillingState = 'trialing' | 'active' | 'past_due' | 'inactive' | 'suspended';

export type Entitlements = {
  plan: PlanId;
  planName: string;
  state: BillingState;
  /** False once the trial/subscription ended or the workspace is suspended: read-only. */
  active: boolean;
  features: PlanFeature[];
  limits: PlanLimits;
  managedBy: 'standalone' | 'hive';
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
};

export type WorkspaceBillingFields = {
  plan: string;
  managedBy?: string | null;
  hiveStatus?: string | null;
  hiveFeatures?: string[] | null;
  hiveLimits?: Record<string, number | null> | null;
  subscriptionStatus?: string | null;
  trialEndsAt?: string | null;
  suspendedAt?: string | null;
  deletedAt?: string | null;
};

const LIVE_STATUSES = new Set(['active', 'trialing', 'past_due']);
const NO_FEATURES: PlanFeature[] = [];

function daysLeft(ends: string | null | undefined, now: Date): number | null {
  if (!ends) return null;
  return Math.max(0, Math.ceil((Date.parse(ends) - now.getTime()) / 86_400_000));
}

function knownFeatures(list: readonly string[]): PlanFeature[] {
  return list.filter((f): f is PlanFeature => (PLAN_FEATURES as readonly string[]).includes(f));
}

function limitsFrom(base: PlanLimits, pushed: Record<string, number | null> | null | undefined): PlanLimits {
  if (!pushed) return { ...base };
  return {
    users: 'users' in pushed ? (pushed.users ?? null) : base.users,
    openMatters: 'openMatters' in pushed ? (pushed.openMatters ?? null) : base.openMatters,
  };
}

/**
 * What a workspace may do right now.
 *  - suspended/deleted (super admin, Hive deprovision): read-only, no features;
 *  - Hive-managed: plan, features, limits and status exactly as last pushed by Hive;
 *  - standalone trial: Growth features for {@link TRIAL_DAYS} days;
 *  - standalone paid plan: the plan while the Stripe subscription is live (no status = comped).
 */
export function entitlements(w: WorkspaceBillingFields, now: Date = new Date()): Entitlements {
  const managedBy = w.managedBy === 'hive' ? 'hive' : 'standalone';
  const base = isPaidPlan(w.plan) ? PLANS[w.plan] : null;
  if (w.suspendedAt || w.deletedAt || (managedBy === 'hive' && w.hiveStatus === 'suspended')) {
    return {
      plan: base?.id ?? 'trial',
      planName: base?.name ?? 'Trial',
      state: 'suspended',
      active: false,
      features: NO_FEATURES,
      limits: limitsFrom(base?.limits ?? PLANS.law_growth.limits, w.hiveLimits),
      managedBy,
      trialEndsAt: w.trialEndsAt ?? null,
      trialDaysLeft: null,
    };
  }
  if (managedBy === 'hive') {
    const def = base ?? PLANS.law_starter;
    const status = (w.hiveStatus ?? 'active') as BillingState;
    const live = LIVE_STATUSES.has(status);
    return {
      plan: def.id,
      planName: def.name,
      state: status,
      active: live,
      features: live ? knownFeatures(w.hiveFeatures ?? def.features) : NO_FEATURES,
      limits: limitsFrom(def.limits, w.hiveLimits),
      managedBy,
      trialEndsAt: w.trialEndsAt ?? null,
      trialDaysLeft: status === 'trialing' ? daysLeft(w.trialEndsAt, now) : null,
    };
  }
  if (base) {
    const status = w.subscriptionStatus ?? null;
    const live = status === null || LIVE_STATUSES.has(status);
    return {
      plan: base.id,
      planName: base.name,
      state: !live ? 'inactive' : status === 'past_due' ? 'past_due' : 'active',
      active: live,
      features: live ? [...base.features] : NO_FEATURES,
      limits: { ...base.limits },
      managedBy,
      trialEndsAt: null,
      trialDaysLeft: null,
    };
  }
  const ends = w.trialEndsAt ?? null;
  const live = !ends || Date.parse(ends) > now.getTime();
  return {
    plan: 'trial',
    planName: 'Trial',
    state: live ? 'trialing' : 'inactive',
    active: live,
    features: live ? [...PLAN_FEATURES] : NO_FEATURES,
    limits: { ...PLANS.law_growth.limits },
    managedBy,
    trialEndsAt: ends,
    trialDaysLeft: daysLeft(ends, now),
  };
}

export function hasFeature(e: Pick<Entitlements, 'features'>, feature: PlanFeature): boolean {
  return e.features.includes(feature);
}

/**
 * API route prefixes that need a plan feature. Matched by prefix so routes added under a prefix
 * later are gated automatically. Order matters: the first match wins (more specific first).
 */
export const FEATURE_PATHS: readonly [prefix: string, feature: PlanFeature][] = [
  ['/api/checkins', 'deadline_checkins'],
  ['/api/document-requests', 'document_requests'],
  ['/api/portal-admin', 'client_portal'],
  ['/api/integrations', 'integrations'],
  ['/api/intake', 'intake_forms'],
  ['/api/matters', 'matters'],
  ['/api/updates', 'case_updates'],
  ['/api/booking', 'booking'],
  ['/api/payments', 'payments'],
];

export function featureForPath(path: string): PlanFeature | null {
  return FEATURE_PATHS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))?.[1] ?? null;
}

/** Writes still allowed while read-only: billing, team, settings, sign-out. */
export function allowedWhenInactive(path: string): boolean {
  return path.startsWith('/api/workspace') || path.startsWith('/api/auth') || path.startsWith('/api/billing');
}

/** Null when another seat may be used, otherwise the reason it may not. */
export function seatLimitReason(e: Pick<Entitlements, 'limits' | 'planName' | 'managedBy'>, usedSeats: number): string | null {
  const max = e.limits.users;
  if (max === null || usedSeats < max) return null;
  return `The ${e.planName} plan includes ${max} team members and all of them are in use (including pending invitations). ${upgradeHint(e)}`;
}

/** Null when another matter may be opened, otherwise the reason it may not. */
export function matterLimitReason(e: Pick<Entitlements, 'limits' | 'planName' | 'managedBy'>, openMatters: number): string | null {
  const max = e.limits.openMatters;
  if (max === null || openMatters < max) return null;
  return `The ${e.planName} plan includes ${max} open matters and this firm has reached it. Close a matter or upgrade. ${upgradeHint(e)}`;
}

export function upgradeHint(e: Pick<Entitlements, 'managedBy'>): string {
  return e.managedBy === 'hive' ? 'Upgrade in Hive.' : 'Upgrade to Growth in Settings → Billing.';
}

export function planRequiredMessage(feature: PlanFeature, e: Pick<Entitlements, 'managedBy'>): string {
  return `${PLAN_FEATURE_LABELS[feature]} is part of LawBee Growth. ${upgradeHint(e)}`;
}

export const INACTIVE_MESSAGE =
  'This workspace is read-only: its trial or subscription has ended, or it was suspended. Your data is kept and can still be viewed and exported.';

/** Whole-dollar plan price, e.g. `$349` (CAD, en-CA). */
export function formatPrice(def: Pick<PlanDefinition, 'priceCents'>): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: def.priceCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(def.priceCents / 100);
}
